import React, { Suspense, useState, useEffect } from 'react';
import { isNil } from 'lodash';
import { Typography, withStyles } from '@material-ui/core';
import scrollIntoView from 'scroll-into-view';
import InfiniteScroll from 'react-infinite-scroll-component';
import { connect } from 'react-redux';
import _ from 'lodash';
import Link from 'next/link';
import EventEmitter from '../../../lib/eventEmitter';
import PopularSearches from '../PopularSearches';
import NoResults from '../../utility/NoResults';
import ToriiLoader from '../../utility/ToriiLoader';
import { LocationDetailType } from '../../../lib/constants';
import {
  extractDisclaimerProperties,
  missingImage,
} from '../../../lib/helpers';
import { isMobile } from '../../../lib/getDeviceSize';
import styles from './styles';
import ListingsComponent from '../ListingsComponent';
import ListingPreview from '../ListingPreview';

export interface Props {
  allowFavorite: boolean;
  classes: any;
  emptyText?: string;
  listings: any[];
  narrow: boolean;
  token: string;
  onListingSelect?: Function;
  photosByRoom?: string;
  popularSearches?: LocationDetailType[] | [];
  searchState?: 'MA' | undefined;
  selectedListings?: any[];
  showPopularSearches?: boolean;
  dispatch: any;
  showDelete: boolean;
  showInModal?: boolean;
  deleted?: boolean;
  isLoved?: boolean;
  hideDisclaimer?: boolean;
  isComps?: boolean;
  pointDialogToSidebar?: boolean;
  useLoadButton?: boolean;
  customLoadButton?: any;
  buttonLoader: boolean;
}

export interface State {
  listings: any[] | null;
  upperListingLimit: number;
  width: number;
}

export const Listings: React.FC<Props> = props => {
  let itemRefs: any = {};
  EventEmitter.on('onMarkerClicked', listing => {
    itemRefs[listing.id] &&
      scrollIntoView(itemRefs[listing.id], {
        align: { top: 1, topOffset: -280 },
        time: 500,
        validTarget: function(target, parentsScrolled) {
          return target.matches && target.matches('.listings-content')
            ? true
            : false;
        },
      });
  });

  const [state, setState] = useState<State>({
    listings: [],
    upperListingLimit: 0,
    width: 0,
  });

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener('resize', updateWindowDimensions);
    if (props.listings.length > 0) {
      limitedListingPreviews(props);
    }
    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
    };
  }, []);

  useEffect(() => {
    if (props.listings && Object.keys(props.listings).length > 0) {
      const limitedPreviews = props.listings.filter(
        (listing: any, index: number) => index < state.upperListingLimit,
      );
      setState((prevState: State) => {
        return {
          ...prevState,
          listings: props.useLoadButton
            ? listingPreviewsLoadButton(props)
            : listingPreviewsInfinteScroll(props, limitedPreviews),
        };
      });
    }
  }, [state.upperListingLimit]);

  useEffect(() => {
    setState((prevState: State) => {
      return {
        ...prevState,
        listings: props.useLoadButton
          ? listingPreviewsLoadButton(props)
          : listingPreviewsInfinteScroll(props, props.listings),
      };
    });
  }, [props.selectedListings && props.selectedListings.length]);

  const updateWindowDimensions = () => {
    setState((previousState: State) => {
      return {
        ...previousState,
        width: window.innerWidth,
      };
    });
  };

  const renderListings = (listing: any, index: number) => {
    const commonProps: any = {
      allowFavorite: props.allowFavorite,
      listing,
      photosByRoom: props.photosByRoom,
    };
    const listingProps: any = !isNil(props.selectedListings)
      ? {
          ...commonProps,
          onCardSelect: props.onListingSelect,
          selected:
            props.selectedListings.filter(
              (selectedlisting: any) => selectedlisting.id === listing.id,
            ).length > 0,
        }
      : commonProps;

    if (props.isComps) {
      return (
        <ListingPreview
          key={`${listing.id}${index}`}
          allowFavorite={false}
          cardFormat
          listing={listingProps.listing}
          disableImageSlider
          {...listingProps}
        />
      );
    }

    const listingList = () => {
      return listingProps.listing.photoMediumList.map(
        (photo: any, index: number) => (
          <div className="slick-slide" key={index}>
            <img
              src={photo}
              className={styles.slickSlideImage}
              alt={`listing-photo--${index}`}
              style={{
                height: isMobile() ? 180 : 230,
                width: 338,
                objectFit: 'cover',
                backgroundImage:
                  'linear-gradient(141deg, #443461 10%, #c8bddb 75%)',
              }}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src = missingImage;
              }}
            />
          </div>
        ),
      );
    };

    return (
      <div
        ref={el => (itemRefs[listing.id] = el)}
        key={`${listing.id}${index}`}
      >
        <ListingsComponent
          listing={listingProps.listing}
          listingList={listingList}
          index={index}
          showDelete={props.showDelete}
          alreadyDeleted={props.deleted}
          isLoved={props.isLoved}
          showInModal={props.showInModal}
          pointDialogToSidebar={props.pointDialogToSidebar}
        />
      </div>
    );
  };

  const listingPreviewsInfinteScroll = (props: Props, listings: any[]) => {
    if (props.listings && Object.keys(props.listings).length > 0) {
      const previews = listings.map((listing: any, index: number) =>
        renderListings(listing, index),
      );
      return previews;
    }
    return null;
  };

  const listingPreviewsLoadButton = (props: Props) => {
    if (props.listings && Object.keys(props.listings).length > 0) {
      const previews = props.listings.map((listing: any, index: number) =>
        renderListings(listing, index),
      );
      return previews;
    }
    return null;
  };

  const limitedListingPreviews = (props: Props) => {
    setState((prevState: State) => {
      return {
        ...prevState,
        upperListingLimit: prevState.upperListingLimit + 10,
      };
    });
  };

  const renderDisclaimer = () => {
    const { classes, listings } = props;
    if (listings.length === 0) {
      return null;
    }
    const disclaimers: any[] = extractDisclaimerProperties(listings);
    if (disclaimers.length === 0) {
      return null;
    }

    return (
      <div className={classes.disclaimerContainer}>
        {disclaimers.map(({ slug, disclaimerText }: any) => (
          <div key={slug.split('.')[0]} style={{ marginTop: 20 }}>
            <img
              alt={`MLS Logo ${_.capitalize(slug.split('.')[0])}`}
              src={`/static/img/mls_sources/${slug}`}
              height="35"
              className={classes.mlsLogo}
            />
            <Typography className={classes.disclaimer}>
              {disclaimerText}
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  const {
    emptyText,
    listings,
    searchState,
    showPopularSearches,
    popularSearches,
    classes,
    hideDisclaimer,
    isComps,
    customLoadButton,
  } = props;
  const { width } = state;
  const mobile = width <= 529;
  const tablet = width > 529 && width <= 949;
  let maxWidth: number | string = 750;
  if (mobile) {
    maxWidth = '100%';
  } else if (tablet) {
    maxWidth = 760;
  }

  return (
    <Suspense fallback={<ToriiLoader />}>
      {listings.length === 0 && (
        <NoResults
          emptyText={
            emptyText ||
            "Oops! Looks like there aren't any homes matching your search. Try removing filters to find more homes."
          }
        />
      )}
      <div
        style={{
          height: '100%',
          margin: '0 auto',
          width: maxWidth,
        }}
        className="row col-xs-12 center-xs"
      >
        {listings.length > 0 && (
          <div
            id="scrollableDiv"
            style={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <InfiniteScroll
              dataLength={state.upperListingLimit}
              next={() => limitedListingPreviews(props)}
              hasMore={true}
              loader={null}
              scrollableTarget="scrollableDiv"
              className={classes.infiniteScrollComponent}
            >
              {state.listings?.map((listing: any) => listing)}
              {showPopularSearches &&
                popularSearches &&
                popularSearches.length > 0 && (
                  <PopularSearches
                    searchState={searchState}
                    popularSearches={popularSearches}
                  />
                )}
              <div>
                {customLoadButton && customLoadButton(props.buttonLoader)}
                {!hideDisclaimer && renderDisclaimer()}
              </div>

              {isComps && (
                <Typography className={classes.compsDisclaimerText}>
                  Disclaimer: This estimate is provided by Torii. It is not an
                  appraisal and it should be used as a starting point. Please{' '}
                  <a style={{ fontWeight: 'bold' }}>
                    <Link href="/contact">contact us</Link>{' '}
                  </a>
                  for more help.
                </Typography>
              )}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </Suspense>
  );
};

const mapStateToProps = (state: any) => ({
  token: state.auth.token,
  buttonLoader: state.listings.loadingMoreListings,
});

export default connect(mapStateToProps)(withStyles(styles)(Listings));
